import { useMemo, useState } from 'react'
import { buildRollup } from '../../helpers/wervingsacties/rollup'
import { NIVEAUS, rollupColumns, celTekst } from '../../helpers/wervingsacties/rollupColumns'
import { straatKey } from '../../helpers/wervingsacties/straatKey'
import buildWervingsRollupExportRows from '../../helpers/export/buildWervingsRollupExportRows'
import downloadCSV from '../../helpers/export/downloadCSV'

// Het overzicht van een wervingsactie (het buurt- en wijkblad uit de Excel van
// de klant), op vier niveaus: wijk, buurt, straat, postcode. De kolommen komen
// uit rollupColumns, dezelfde bron als de export.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10 (2))

/**
 * @param {object} args
 * @param {Object<string, string>} args.wijkNaam - WijkCode → naam uit de actie
 * @param {Object<string, string>} args.buurtNames - BuurtCode → naam (useBuurtNames)
 * @param {boolean} args.meerWoonplaatsen - Of de actie adressen in meer dan één woonplaats heeft
 * @returns {Object<string, { keyFn: (r: object) => string, labelFn: (r: object) => string }>}
 */
const groeperingen = ({ wijkNaam, buurtNames, meerWoonplaatsen }) => ({
    wijk: { keyFn: (r) => r.WijkCode || '', labelFn: (r) => wijkNaam[r.WijkCode] || r.WijkCode || 'Overig' },
    buurt: { keyFn: (r) => r.BuurtCode || '', labelFn: (r) => buurtNames?.[r.BuurtCode] || r.BuurtCode || 'Overig' },
    // Dezelfde straatnaam in twee woonplaatsen zijn twee straten (straatKey);
    // dan hoort de woonplaats in de naam, anders staan er twee gelijke regels.
    straat: {
        keyFn: (r) => straatKey(r),
        labelFn: (r) => (r.Straatnaam ? (meerWoonplaatsen && r.Woonplaats ? `${r.Straatnaam} (${r.Woonplaats})` : r.Straatnaam) : 'Overig'),
    },
    postcode: { keyFn: (r) => r.Postcode || '', labelFn: (r) => r.Postcode || 'Zonder postcode' },
})

/**
 * @param {object} props
 * @param {Array<object>} props.registraties - De levende Wervingsregistraties
 * @param {Array<{ WijkCode: string, Naam: string }>} props.wijken - De gekozen wijken van de actie
 * @param {Object<string, string>} props.buurtNames - BuurtCode → naam (useBuurtNames)
 * @param {Array<object>} props.adressen - Alle adressen in het gebied (useWijkAdressen)
 * @param {'idle' | 'loading' | 'complete' | 'failed'} props.adressenStand - Alleen bij 'complete' zijn de woningen in het gebied bekend
 * @param {Array<{ key: string, label: string }>} props.diensten - De zichtbare diensten van de organisatie
 * @param {Object<string, string>} props.dienstNamen - Sleutel → naam, ook van verborgen diensten
 * @param {Object<string, { Tekst?: string }>} props.straatOpmerkingen - Wervingsacties.StraatOpmerkingen
 * @param {string} props.titel - Naam van de wervingsactie, voor de bestandsnaam
 * @param {'idle' | 'loading' | 'complete' | 'failed'} props.buurtNamenStand - Laadstand van de buurtnamen (useBuurtNames)
 */
const Rollup = ({ registraties, wijken, buurtNames, adressen, adressenStand, diensten, dienstNamen, straatOpmerkingen, titel, buurtNamenStand }) => {
    const [niveau, setNiveau] = useState('wijk')
    const [downloadMelding, setDownloadMelding] = useState('')

    const wijkNaam = useMemo(() => Object.fromEntries((wijken || []).map((w) => [w.WijkCode, w.Naam])), [wijken])

    // Alleen een complete lijst telt als 'woningen in het gebied'; anders onbekend.
    const gebied = adressenStand === 'complete' ? adressen : null

    const meerWoonplaatsen = useMemo(
        () => new Set([...(registraties || []), ...(gebied || [])].map((r) => r.Woonplaats).filter(Boolean)).size > 1,
        [registraties, gebied],
    )

    const rollup = useMemo(() => {
        const { keyFn, labelFn } = groeperingen({ wijkNaam, buurtNames, meerWoonplaatsen })[niveau]
        return buildRollup(registraties || [], keyFn, { labelFn, adressen: gebied })
    }, [registraties, wijkNaam, buurtNames, meerWoonplaatsen, niveau, gebied])

    const kolommen = useMemo(() => rollupColumns({
        niveau,
        diensten: diensten || [],
        dienstNamen: dienstNamen || {},
        gebruikteDiensten: Object.keys(rollup.totals.diensten),
        straatOpmerkingen: straatOpmerkingen || {},
    }), [niveau, diensten, dienstNamen, rollup, straatOpmerkingen])

    const totaalRij = { ...rollup.totals, key: '', label: 'Totaal' }

    // Met een halve lijst uit het register ontbreken de woningen in het gebied,
    // en zo'n bestand gaat naar een opdrachtgever. Mislukt het laden, dan mag
    // het wel: de kolom is dan leeg, niet te laag.
    // AI-CONTEXT: See docs/pitfalls.md#ui-export-claims-everything-on-screen
    const lijstLaadt = adressenStand === 'loading'
    // Zonder namen staan er buurtcodes in het bestand; na een mislukte lijst
    // mag het wel (codes zijn waar, alleen minder leesbaar).
    const namenLaden = buurtNamenStand === 'loading'

    const downloaden = () => {
        const { rows, header } = buildWervingsRollupExportRows({ kolommen, totals: totaalRij, rows: rollup.rows })
        const niveauNaam = NIVEAUS.find((n) => n.id === niveau)?.label.toLowerCase() || niveau
        const datum = new Date().toISOString().slice(0, 10)
        const gelukt = downloadCSV(rows, `Wervingsactie ${titel || ''} ${niveauNaam} ${datum}.csv`.replace(/\s+/g, ' '), header)
        setDownloadMelding(gelukt ? '' : 'Er was niets om te downloaden.')
    }

    return (
        <div className='wervingsacties-rollup'>
            <div className='wa-niveaus' role='group' aria-label='Indeling'>
                {NIVEAUS.map((n) => (
                    <button
                        key={n.id}
                        type='button'
                        className={niveau === n.id ? 'wa-toggle wa-toggle-on' : 'wa-toggle'}
                        aria-pressed={niveau === n.id}
                        onClick={() => setNiveau(n.id)}
                    >
                        {n.label}
                    </button>
                ))}
            </div>
            <div className='wa-download'>
                <button type='button' onClick={downloaden} disabled={lijstLaadt || namenLaden}>Downloaden (CSV)</button>
                {lijstLaadt && (
                    <span className='wa-hint'>Even wachten: de adressen uit het register worden nog geladen.</span>
                )}
                {!lijstLaadt && namenLaden && (
                    <span className='wa-hint'>Even wachten: de buurtnamen worden nog geladen.</span>
                )}
                {downloadMelding && <span className='wa-hint' role='status'>{downloadMelding}</span>}
            </div>
            <p className='wa-hint'>
                Brief gepost = % van de gekozen woningen · Deurgesprek = % van geposte woningen · Ja, nee en misschien = % van
                deurgesprekken · Huur en koop = % van de gekozen woningen
            </p>
            {rollup.rows.length === 0 ? (
                <p className='wa-empty'>Nog geen registraties.</p>
            ) : (
                <div className='wa-rollup-scroll'>
                    <table className='wa-rollup-table'>
                        <thead>
                            <tr>
                                {kolommen.map((k) => <th key={k.key}>{k.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='wa-rollup-total'>
                                {kolommen.map((k) => (
                                    <td key={k.key}>{k.alleenPerRegel ? '' : celTekst(k, totaalRij)}</td>
                                ))}
                            </tr>
                            {rollup.rows.map((row) => (
                                <tr key={row.key || 'overig'}>
                                    {kolommen.map((k) => <td key={k.key}>{celTekst(k, row)}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export default Rollup
